import { useRouter } from "next/router";
import Image from "next/image";
import Link from "next/link";
import dynamic from "next/dynamic";
import { Flex, Layout, Spin, Dropdown } from "antd";
// OPTIMIZED: Replaced @ant-design/icons with lightweight SVGs
import { UserIcon, LogoutIcon, CalendarIcon } from "../Icons";

// Dynamic import SearchBar to avoid SSR issues with LocationContext
const SearchBar = dynamic(
  () => import("../../page/HomePage/TileContainer/Search"),
  { 
    ssr: false, // Client-only to avoid LocationContext hydration issues
    loading: () => null
  }
);
import { Dispatch, SetStateAction, useState, useEffect, useMemo } from "react";

// Dynamic import SelectLocation to avoid SSR issues with LocationContext
const SelectLocation = dynamic(
  () => import("../SelectLocation"),
  { 
    ssr: false // Client-only to avoid LocationContext hydration issues
  }
);
import { useTurfContext } from "../../contexts/TurfContext";
import CustomTabs from "../CustomTabs";
import constants from "../../utils/constants";
import { useAuth } from "../../contexts/AuthContext";
import { getCanonicalCitySlug } from "../../config/cityData";

// Dynamic import AuthModal to avoid SSR issues
const AuthModal = dynamic(() => import("../AuthModal"), { ssr: false });

const { Header } = Layout;

function HeaderComponent({
  name,
  setCity,
  setSearch,
  turfData,
  activeTab,
  handleTabChange,
  pageType,
  city,
}: {
  name: string | null;
  setCity: Dispatch<SetStateAction<string>>;
  setSearch: React.Dispatch<React.SetStateAction<string>>;
  turfData: any[];
  activeTab?: string;
  handleTabChange?: (key: string) => void;
  pageType?: 'court' | 'single-turf' | 'listing';
  city?: string;
}) {
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [isLoadingCities, setIsLoadingCities] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [hasMounted, setHasMounted] = useState(false);
  const router = useRouter();
  const { currentTurfLogo, availableSportTabs } = useTurfContext();
  const { user, isAuthenticated, logout } = useAuth();
  const canRenderInteractiveHeader = hasMounted && router.isReady;
  
  // Use router.isReady to check if client-side routing is ready
  // This is safer for hydration as it's consistent between server and client initial render
  const isReady = router.isReady;
  
  // Conditionally render the SearchBar - show on home page and city pages
  const currentPath = canRenderInteractiveHeader
    ? (router.asPath || router.pathname || "")
    : (router.pathname || "");
  const currentPathWithoutQuery = currentPath.split("?")[0]?.split("#")[0] || currentPath;
  const isHomePage = currentPathWithoutQuery === "/" || currentPathWithoutQuery === "/index";
  // City pages (/indore) and area-listing pages (/indore/area/x) both show full header.
  const pathSegmentCount = currentPathWithoutQuery
    .split('/')
    .filter(Boolean).length;
  const isCityListingPathname =
    router.pathname === "/[city]" ||
    router.pathname === "/[city]/area/[area]";
  const isCityListingDepth =
    pathSegmentCount === 1 ||
    (pathSegmentCount === 3 && currentPathWithoutQuery.includes('/area/'));
  const isCityPage = isCityListingPathname &&
                     currentPathWithoutQuery !== "/noData" &&
                     !currentPathWithoutQuery.includes('/tournament') &&
                     !currentPathWithoutQuery.includes('/booking') &&
                     !currentPathWithoutQuery.includes('/about') &&
                     !currentPathWithoutQuery.includes('/privacy') &&
                     !currentPathWithoutQuery.includes('/terms') &&
                     isCityListingDepth;
  
  const renderSearchBar = canRenderInteractiveHeader && isCityPage && !name;
  const renderSelectCity =
    canRenderInteractiveHeader &&
    (isHomePage || isCityPage || currentPathWithoutQuery === "/noData") &&
    !name;

  // Memoized navigation visibility checks
  const isValidPage = !['single-turf', 'court'].includes(pageType || '');
  
  // Get city from URL pathname for ISR pages (consistent server/client)
  // This prevents hydration mismatch caused by _app.tsx returning different city on server vs client
  const cityFromUrl = useMemo(() => {
    if (
      isCityPage &&
      currentPathWithoutQuery &&
      currentPathWithoutQuery !== "/" &&
      currentPathWithoutQuery !== "/index"
    ) {
      const pathParts = currentPathWithoutQuery.split('/').filter(Boolean);
      const isAreaListing =
        pathParts.length === 3 && pathParts[1]?.toLowerCase() === 'area';
      if (
        (pathParts.length === 1 || isAreaListing) &&
        pathParts[0] !== 'noData'
      ) {
        return getCanonicalCitySlug(pathParts[0]);
      }
    }
    return null;
  }, [isCityPage, currentPathWithoutQuery]);
  
  // Use city from URL for ISR pages, fallback to prop for other pages
  // This ensures server and client use the same city value
  const effectiveCity = useMemo(() => {
    if (isCityPage && cityFromUrl) {
      return cityFromUrl; // Use URL city for ISR pages (consistent)
    }
    return city; // Use prop for other pages
  }, [isCityPage, cityFromUrl, city]);
  
  // Only check hasCourts if city is available and confirmed (not default/undefined)
  // This prevents showing tabs initially when city is still being determined
  // For ISR pages, use city from URL which is consistent between server and client
  const isCityConfirmed = useMemo(() => {
    const cityToCheck = effectiveCity;
    if (!cityToCheck) return false;
    
    if (
      isCityPage &&
      (router.pathname === "/[city]" ||
        router.pathname === "/[city]/area/[area]")
    ) {
      // If router query is available, verify match (client-side navigation)
      if (router.isReady && router.query.city) {
        const urlCity = Array.isArray(router.query.city) 
          ? getCanonicalCitySlug(router.query.city[0] || "") 
          : getCanonicalCitySlug(router.query.city || "");
        return urlCity === getCanonicalCitySlug(cityToCheck);
      }
      // For ISR pre-rendered pages, city from URL is always valid
      // router.isReady is false during SSR/ISR, but URL pathname is consistent
      // This prevents hydration mismatch for ISR pages
      return true;
    }
    
    // For other pages, wait for router to be ready
    if (!router.isReady) return false;
    return true;
  }, [effectiveCity, router.isReady, router.query.city, router.pathname, isCityPage]);
  
  // The listing page publishes the sports it actually has turfs for; a single
  // sport needs no tab bar.
  const hasMultipleSports = useMemo(() => {
    if (!effectiveCity || !isCityConfirmed) return false;
    return availableSportTabs.length > 1;
  }, [effectiveCity, isCityConfirmed, availableSportTabs]);

  const showNavigation = canRenderInteractiveHeader && isCityPage && isValidPage && isCityConfirmed && hasMultipleSports && activeTab && handleTabChange;
  const isIndore = effectiveCity?.toLowerCase() === constants.DEFAULT_CITY.toLowerCase();

  // Add/remove body class based on tabs visibility for CSS targeting
  useEffect(() => {
    setHasMounted(true);
  }, []);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      if (!showNavigation) {
        document.body.classList.add('header-no-tabs');
      } else {
        document.body.classList.remove('header-no-tabs');
      }
      // Cleanup on unmount
      return () => {
        document.body.classList.remove('header-no-tabs');
      };
    }
  }, [showNavigation]);

  const handleHeaderClick = () => {
    try {
      const targetPath = "/";
      
      if (router.isReady) {
        router.push(targetPath).catch((error) => {
          console.error("Navigation error:", error);
          // Fallback: use window.location if router fails
          if (typeof window !== 'undefined') {
            window.location.href = targetPath;
          }
        });
      } else {
        // Router not ready, use window.location
        if (typeof window !== 'undefined') {
          window.location.href = targetPath;
        }
      }
    } catch (error) {
      console.error("Header click error:", error);
      // Final fallback
      if (typeof window !== 'undefined') {
        window.location.href = "/";
      }
    }
  };

  const closeModal = () => {
    setShowLocationModal(false);
  };

  const imgMeta = `${name || ''} | turfBooking.in`;
  const shouldShowTurfLogo = canRenderInteractiveHeader && !!currentTurfLogo;

  const handleShowLocation = () => {
    setShowLocationModal(true);
  };

  const goToTournamentDetails = () => {
    router.push("/tournament");
  };

  // Profile icon - render consistently to avoid hydration mismatch
  // Use suppressHydrationWarning for content that depends on client state
  const renderProfileIcon = () => {
    const profileIconSize = 20;

    if (!canRenderInteractiveHeader) {
      return (
        <div className="profile-icon" aria-hidden="true">
          <UserIcon size={profileIconSize} color="#391085" />
        </div>
      );
    }

    if (isAuthenticated && user) {
      return (
        <Dropdown
          menu={{
            items: [
              { key: "name", label: <span style={{ fontWeight: 600 }}>{user?.name}</span>, disabled: true },
              { key: "phone", label: <span style={{ color: "#888" }}>+91 {user?.phoneNumber}</span>, disabled: true },
              { type: "divider" },
              { key: "bookings", label: <Link href="/my-bookings" style={{ color: "inherit", textDecoration: "none" }}>My Bookings</Link>, icon: <CalendarIcon size={14} /> },
              { key: "logout", label: "Logout", icon: <LogoutIcon size={14} />, onClick: logout },
            ],
          }}
          trigger={["click"]}
          placement="bottomRight"
        >
          <div
            className="profile-icon"
            role="button"
            tabIndex={0}
            aria-label="Account menu"
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                (e.currentTarget as HTMLElement).click();
              }
            }}
          >
            <UserIcon size={profileIconSize} color="#391085" />
          </div>
        </Dropdown>
      );
    }
    return (
      <div
        className="profile-icon"
        role="button"
        tabIndex={0}
        aria-label="Sign in"
        onClick={() => setShowAuthModal(true)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setShowAuthModal(true);
          }
        }}
      >
        <UserIcon size={profileIconSize} color="#391085" />
      </div>
    );
  };

  return (
    <div className="header-wrapper">
      {/* Top Header Section */}
      <Header className="header-top">
        <div className="header-content" onClick={handleHeaderClick}>
          {shouldShowTurfLogo ? (
            <Flex align="center" gap={16}>
              <Image
                src={currentTurfLogo as string}
                alt={imgMeta}
                title={imgMeta}
                className="turf-logo-image"
                width={50}
                height={50}
                unoptimized
                priority
              />
              <Image
                src={"/LogoText.png"}
                alt="TurfBooking.in - Book Cricket & Football Turfs Online"
                title={imgMeta}
                className="logo-image"
                width={200}
                height={50}
                style={{ width: 'auto', height: 'auto' }}
                priority
              />
            </Flex>
          ) : (
            <Image
              src={"/LogoText.png"}
              alt="TurfBooking.in - Book Cricket & Football Turfs Online"
              title={imgMeta}
              className="logo-image"
              width={200}
              height={50}
              style={{ width: 'auto', height: 'auto' }}
              priority
            />
          )}
        </div>

        {/* Search + Icons Container */}
        {/* Use suppressHydrationWarning to prevent mismatch from conditional rendering */}
        <div className="search-location" suppressHydrationWarning>
          {renderSearchBar && (
            <SearchBar setSearch={setSearch} />
          )}

          {!isHomePage && renderSelectCity && (
            <div className="location-icon" onClick={handleShowLocation}>
              {isLoadingCities ? (
                <Spin size="small" />
              ) : (
                <Image src="/Icon/gps.svg" alt="Location" width={24} height={24} unoptimized />
              )}
            </div>
          )}

          {!isHomePage && (
            <div suppressHydrationWarning>
              {renderProfileIcon()}
            </div>
          )}

        </div>

        {showLocationModal && (
          <SelectLocation
            setCity={setCity}
            visible={true}
            onClose={closeModal}
            onLoadingChange={setIsLoadingCities}
          />
        )}

        {showAuthModal && (
          <AuthModal visible={true} onClose={() => setShowAuthModal(false)} />
        )}
      </Header>
      
      {/* Bottom Navigation Section - only for cities that list more than one sport */}
      {/* Always render wrapper to prevent hydration mismatch, content conditionally rendered */}
      <div suppressHydrationWarning>
        {showNavigation && (
          <div className="header-navigation">
            <CustomTabs
              activeKey={activeTab}
              onChange={handleTabChange}
              availableKeys={availableSportTabs}
            />
            {/* {isIndore && (
              <button className="tournament-cta" onClick={goToTournamentDetails}>
                <span className="trophy" aria-hidden>🏆</span>
                <span className="label">Tournament</span>
              </button>
            )} */}
          </div>
        )}
      </div>
    </div>
  );
}

export default HeaderComponent;
